import React, { useEffect, useState } from "react";
import { Button, Col, Image, Row, Stack } from "react-bootstrap";
import { Form, Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LOGIN_BANNER from "../../assets/images/login-banner.svg";
import LOGO_WHITE from "../../assets/images/logo.svg";
import CommonFormikComponent from "../../components/common/CommonFormikComponent";
import CustomInputText from "../../components/common/CustomInputText";
import Loader from "../../components/common/loader";
import storage from "../../helpers/storage";
import { forgotPasswordAPI } from "../../services/auth.service";
import { ForgotSchema } from "./validation";
import GoogleCaptcha from "../../components/common/googleCaptcha";
import { GOOGLE_CAPTCHA_RESPONSE_KEY } from "../../constants";
import { getCaptchaToken } from "../../helpers/utils";
import { useSettingStore } from "../../redux/auth-store";
import SvgIcons from "../../components/common/SvgIcons";

/**
 * Form values type definition.
 */
type FormValues = {
  email: string;
};

export default function ForgotPassword() {
  const AppName = process.env.REACT_APP_NAME;
  const {
    settings: { ENABLE_RECAPTCHA, RECAPTCHA_SITE_KEY },
  } = useSettingStore();

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  /**
   * Initial Form Values
   */
  const initialValues: FormValues = {
    email: "",
  };

  /**
   * Hook to check if user is already login
   */
  useEffect(() => {
    if (storage.getToken() != null) {
      return navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  /**
   * Submit Method to call when user clicks on forgot button
   * @async
   * @param {FormValues} values
   * @param {any} actions
   * @returns {void}
   */
  const handleSubmit = async (values: Record<string, any>, actions: any) => {
    setLoading(true);
    actions.setSubmitting(true);
    const formData: any = {
      email: values.email,
    };

    /**
     * set captcha if captcha is enabled.
     */
    if (ENABLE_RECAPTCHA) {
      await getAndSetCaptchaToken(formData);
    }

    forgotPasswordAPI(formData)
      .then((response: any) => {
        setLoading(false);
        actions.setSubmitting(false);
        toast(response.msg, { type: toast.TYPE.SUCCESS });
        actions.resetForm(initialValues);
        navigate("/login");
      })
      .catch((error: any) => {
        setLoading(false);
        actions.setSubmitting(false);
        toast(error.data.msg, { type: toast.TYPE.ERROR });
      });
  };

  /**
   * Retrieves and sets the captcha token in the provided form data.
   * @param {Object} formData - The form data object.
   * @returns {Promise<void>}
   */
  const getAndSetCaptchaToken = async (formData: any) => {
    const captchaToken: string = await getCaptchaToken(RECAPTCHA_SITE_KEY);
    if (captchaToken) {
      formData[GOOGLE_CAPTCHA_RESPONSE_KEY] = captchaToken;
    }
  };

  return (
    <React.Fragment>
      <Loader isLoading={loading} />
      <Row className="g-0 vh-100">
        <Col md={6}>
          <Stack
            className="custom-primary-gradient text-center justify-content-around h-100 p-4"
            gap={4}
          >
            <div className="user-select-none">
              <Image
                fluid={true}
                src={LOGO_WHITE}
                alt={`${AppName} Logo`}
                width={158}
                height={48}
              />
            </div>
            {/* <div>
                            <Image
                                fluid={true}
                                src={LOGIN_BANNER}
                                alt={`${AppName} Customer Panel`}
                                width={389}
                                height={334}
                            />
                        </div> */}
            {/* <div className='lh-sm text-white'>
                            <div className='fw-light fs-4'>Advanced Dynamic</div>
                            <div className='fw-semibold fs-1 text-uppercase'>Vulnerability Scanning</div>
                            <div className='fw-light fs-4'>for Enterprises</div>
                        </div> */}
          </Stack>
        </Col>
        <Col md={6} className="col-md-6 d-flex flex-column p-3">
          <div className="custom-width-320 d-flex flex-column flex-grow-1 mw-100 mx-auto py-3">
            <div className="mt-72">
              <div className=" custom-min-height-40 fs-13 py-2 rounded  w-100 d-inline-flex align-items-center mb-5">
                <Link className="fw-semibold text-decoration-none" to="/login">
                  {SvgIcons.LeftArrowIcon}
                </Link>
                <span className="ps-1">Back </span>
              </div>
              <h1 className="fw-semibold mb-1 pb-1  fs-2">Forgot Password</h1>
              <p className="mb-1 opacity-75 fs-14 mb-4">
                Please enter your registered email
              </p>
              <CommonFormikComponent
                validationSchema={ForgotSchema}
                initialValues={initialValues}
                onSubmit={handleSubmit}
              >
                {(formikProps) => (
                  <>
                    <CustomInputText
                      wrapperClassname="mb-3 pb-1"
                      controlId="email"
                      label="Email Address"
                      placeholder="john.smith@example.com"
                      touched={formikProps.touched.email}
                      handleBlur={formikProps.handleBlur}
                      handleChange={formikProps.handleChange}
                      errorsField={formikProps.errors.email}
                      value={formikProps.values.email}
                      validationSchema={ForgotSchema}
                    />

                    <div className="mb-0 pb-0">
                      <Row className="align-items-center justify-content-between ps-2">
                        <GoogleCaptcha />
                      </Row>
                    </div>
                    <Stack
                      direction="horizontal"
                      gap={3}
                      className="justify-content-end"
                    >
                      <Button
                        className="w-100"
                        variant="primary"
                        type="submit"
                        disabled={formikProps.isSubmitting || loading}
                      >
                        {loading ? "Processing..." : "Send"}
                      </Button>
                    </Stack>
                  </>
                )}
              </CommonFormikComponent>
            </div>
          </div>
        </Col>
      </Row>
    </React.Fragment>
  );
}
